import { useEffect, useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Minus, Plus } from 'lucide-react'
import {
  MANUAL_MOVEMENT_REASONS, MOVEMENT_REASONS, stockAdjustmentSchema, stockCountSchema,
  type MovementReason, type ProductListItem,
} from '@najah/core'
import { Alert, Button, Dialog, Input, Select, Textarea, cn, useToast } from '@najah/ui'
import { inventoryApi } from '../lib/catalogApi'
import { useFormErrors } from '../lib/useFormErrors'

type Mode = 'adjust' | 'count'

interface Props {
  open: boolean
  onClose: () => void
  product: Pick<ProductListItem, 'id' | 'nameAr' | 'sku' | 'unit' | 'quantity'> | null
}

export function StockAdjustDialog({ open, onClose, product }: Props) {
  const toast = useToast()
  const queryClient = useQueryClient()
  const { errors, formError, clear, validate, handleApiError } = useFormErrors()

  const [mode, setMode] = useState<Mode>('adjust')
  const [direction, setDirection] = useState<1 | -1>(1)
  const [amount, setAmount] = useState('')
  const [countedQty, setCountedQty] = useState('')
  const [reason, setReason] = useState<MovementReason>('ADJUSTMENT')
  const [note, setNote] = useState('')

  useEffect(() => {
    if (!open) return
    clear()
    setMode('adjust')
    setDirection(1)
    setAmount('')
    setCountedQty(product ? String(product.quantity) : '')
    setReason('ADJUSTMENT')
    setNote('')
  }, [open, product, clear])

  const mutation = useMutation({
    mutationFn: async () => {
      if (!product) throw new Error('NO_PRODUCT')

      if (mode === 'count') {
        const parsed = validate(stockCountSchema, {
          productId: product.id,
          countedQty: Number(countedQty),
          note: note.trim(),
        })
        if (!parsed) throw new Error('VALIDATION')
        const res = await inventoryApi.count(parsed)
        return {
          quantity: res.quantity,
          message: res.difference === 0
            ? 'الرصيد مطابق — لم تُسجَّل أي حركة'
            : `سُجّلت تسوية بمقدار ${res.difference > 0 ? '+' : ''}${res.difference}`,
        }
      }

      const parsed = validate(stockAdjustmentSchema, {
        productId: product.id,
        qtyDelta: direction * (Number(amount) || 0),
        reason,
        note: note.trim(),
      })
      if (!parsed) throw new Error('VALIDATION')
      const res = await inventoryApi.adjust(parsed)
      return { quantity: res.quantity, message: `الرصيد الجديد ${res.quantity} ${product.unit}` }
    },
    onSuccess: ({ message }) => {
      void queryClient.invalidateQueries({ queryKey: ['products'] })
      void queryClient.invalidateQueries({ queryKey: ['inventory'] })
      toast.success('تم تحديث المخزون', message)
      onClose()
    },
    onError: (err) => {
      if (err instanceof Error && err.message === 'VALIDATION') return
      handleApiError(err, 'تعذّرت تسوية المخزون')
    },
  })

  if (!product) return null

  const delta = direction * (Number(amount) || 0)
  const projected = mode === 'count' ? Number(countedQty) || 0 : product.quantity + delta

  return (
    <Dialog
      open={open}
      onClose={onClose}
      size="md"
      title="تسوية المخزون"
      description={`${product.nameAr} — ${product.sku}`}
      dismissible={!mutation.isPending}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={mutation.isPending}>إلغاء</Button>
          <Button onClick={() => mutation.mutate()} loading={mutation.isPending}>تسجيل الحركة</Button>
        </>
      }
    >
      <div className="space-y-5">
        {formError && <Alert tone="danger">{formError}</Alert>}

        <div className="rounded-lg bg-bg-subtle px-4 py-3 text-sm">
          الرصيد الحالي:{' '}
          <span className="font-semibold text-fg" dir="ltr">{product.quantity}</span> {product.unit}
        </div>

        {/* اختيار الأسلوب: فرق مباشر أم جرد بعدد فعلي */}
        <div className="grid grid-cols-2 gap-2 rounded-lg bg-bg-subtle p-1">
          {([['adjust', 'إضافة / خصم'], ['count', 'جرد (عدد فعلي)']] as const).map(([key, label]) => (
            <button
              key={key}
              type="button"
              onClick={() => setMode(key)}
              className={cn(
                'rounded-md py-2 text-sm font-medium transition-colors',
                mode === key ? 'bg-surface text-fg shadow-sm' : 'text-fg-muted hover:text-fg',
              )}
            >
              {label}
            </button>
          ))}
        </div>

        {mode === 'adjust' ? (
          <>
            <div className="flex gap-2">
              <div className="flex overflow-hidden rounded-lg border border-border">
                <button
                  type="button"
                  onClick={() => setDirection(1)}
                  className={cn('flex items-center gap-1.5 px-3 text-sm transition-colors',
                    direction === 1 ? 'bg-success text-white' : 'text-fg-muted hover:bg-surface-hover')}
                >
                  <Plus className="size-4" /> إدخال
                </button>
                <button
                  type="button"
                  onClick={() => setDirection(-1)}
                  className={cn('flex items-center gap-1.5 border-s border-border px-3 text-sm transition-colors',
                    direction === -1 ? 'bg-danger text-white' : 'text-fg-muted hover:bg-surface-hover')}
                >
                  <Minus className="size-4" /> إخراج
                </button>
              </div>

              <Input
                type="number" min="1" ltr placeholder="الكمية"
                value={amount} onChange={(e) => setAmount(e.target.value)}
                error={errors.qtyDelta}
              />
            </div>

            <Select
              label="السبب" required
              value={reason} onChange={(e) => setReason(e.target.value as MovementReason)}
              options={MANUAL_MOVEMENT_REASONS.map((r) => ({ value: r, label: MOVEMENT_REASONS[r] }))}
              error={errors.reason}
            />
          </>
        ) : (
          <Input
            label="العدد الفعلي في الرفّ" required type="number" min="0" ltr
            value={countedQty} onChange={(e) => setCountedQty(e.target.value)}
            error={errors.countedQty}
            hint="يُسجّل النظام الفرق كحركة تسوية — الرصيد لا يُكتب مباشرة أبداً"
          />
        )}

        <Textarea
          label="ملاحظة" rows={2}
          value={note} onChange={(e) => setNote(e.target.value)}
          error={errors.note}
          placeholder="سبب التسوية، رقم المستند…"
        />

        {(mode === 'count' || amount) && (
          <Alert tone={projected < 0 ? 'danger' : 'info'}>
            الرصيد بعد الحركة:{' '}
            <span className="font-semibold" dir="ltr">{projected}</span> {product.unit}
            {projected < 0 && ' — الرصيد السالب مرفوض، ستُلغى العملية'}
          </Alert>
        )}
      </div>
    </Dialog>
  )
}
